import { Component, Input, OnInit } from '@angular/core';
import { C, Cookies } from '@shared/commons';
import { EstadoTareaService, CompaniaConfiguracionService } from '@shared/services';

import * as moment from 'moment';
import { Observable,forkJoin } from 'rxjs';
import { map, tap } from 'rxjs/operators';

const groupBy = (xs: any[], key: ((item: any) => string) | string) => {
  return xs.reduce((rv, x) => {
    const k = typeof key === 'function' ? (key as (item: any) => string)(x) : x[key as string];
    (rv[k] = rv[k] || []).push(x);
    return rv;
  }, {} as { [k: string]: any[] });
};

const getEstadoTareaToString = (estado, isTask: boolean = false) => {
  const etiquetas = JSON.parse(localStorage.getItem("etiquetas"));
  if (estado != null) {
    switch (estado) {
      case C.TAREA.ESTADO.PENDIENTE:
        return etiquetas['estado_tarea_pendiente'];
      case C.TAREA.ESTADO.EN_APT:
        return etiquetas['estado_tarea_en_apt'];
      case C.TAREA.ESTADO.EN_RUTA:
        return etiquetas['estado_tarea_en_ruta'];
      case C.TAREA.ESTADO.EN_ESPERA:
        return etiquetas['estado_tarea_en_espera'];
      case C.TAREA.ESTADO.EN_CLIENTE ||  C.TAREA.ESTADO.INICIO_CARGA:
        if(isTask) return "Inicio de Carga"
        else return etiquetas['estado_tarea_en_cliente'];
      case C.TAREA.ESTADO.REALIZANDO_TAREA ||  C.TAREA.ESTADO.FIN_CARGA:
        if(isTask) return "Fin de Carga"
        else return etiquetas['estado_tarea_inicio_descarga'];
      case C.TAREA.ESTADO.TERMINADO_TOTAL:
      case C.TAREA.ESTADO.EN_HUB:
        return 'Terminado total';
      case C.TAREA.ESTADO.TERMINADO_PARCIAL:
        return etiquetas['estado_tarea_terminado_parcial'];
      case C.TAREA.ESTADO.RECHAZADO:
        return etiquetas['estado_tarea_rechazado'];
      case C.TAREA.ESTADO.NO_REALIZADO:
        return etiquetas['estado_tarea_no_realizado'];
      case C.TAREA.ESTADO.EN_CAMINO:
        return etiquetas['estado_tarea_en_camino'];
      case C.TAREA.ESTADO.NO_PROGRAMADO:
        return etiquetas['estado_tarea_no_programado'];
    }
  }
  return "";
}

@Component({
  selector: 'historial-estado-tarea',
  templateUrl: './historial-estado-tarea.component.html',
  styleUrls: ['./historial-estado-tarea.component.scss']
})
export class HistorialEstadoTareaComponent implements OnInit {

  @Input() showTitle = false;

  codigoRastreo: string;
  historialEstados$: Observable<any>;
  estadoTarea: any[];
  lng: string;

  constructor(private estadoTareaService: EstadoTareaService, private companiaConfiguracionService: CompaniaConfiguracionService) { }

  ngOnInit(): void {
    this.lng = Cookies.getCookie("LANGUAGE");  
    moment.locale(this.lng || 'es');
  }

  cargarDatos(codigoRastreo: string, tareaId: number) {
    this.codigoRastreo = codigoRastreo;
    //let estadosTarea = this.estadoTareaService.estadosTarea(tareaId);
    let estadosOrden = [1, 2, 5, 6, 3, 4];

this.historialEstados$ = forkJoin([
  this.estadoTareaService.historial(tareaId),
  this.estadoTareaService.estadosTarea(tareaId)
]).pipe(
  map(([res, estadosTarea]) => {
    // Filtras los estados extras de una vez
    const estadosFormateados = estadosTarea
      .filter(s => s.estado == 5 || s.estado == 6)
      .map(e => ({
        fecha: e?.fecha,
        estado: e?.estado,
        distrito: e?.distrito,
        provincia: e?.provincia,
        departamento: e?.departamento,
        usuarioNombre: e?.usuario?.usuario,
        perfiles: e?.perfiles,
        motivoId: e?.motivoId,
        motivo: e?.motivo,
        isTask: true
      }));

    let mostrarApt = true;
    let mostrarInicioCarga = true;
    let mostrarFinCarga = true;
    try {
      const dataConf = this.companiaConfiguracionService.obtenerConfiguraciones('SIN_PREFIJO');
      if (dataConf) {
        const obtenerEstadoConfiguracion = (campo: string) => {
          const config = dataConf.find(c => c.campo === campo);
          return config ? ((config.estado as any) == 1 || (config.estado as any) === true) : true;
        };
        mostrarApt = obtenerEstadoConfiguracion('EST_APT');
        mostrarInicioCarga = obtenerEstadoConfiguracion('EST_INI_CAR');
        mostrarFinCarga = obtenerEstadoConfiguracion('EST_FIN_CAR');
      }
    } catch (e) {
      console.error(e);
    }

    // Combinas y ordenas en un solo paso
    const combinados = [...(res || []), ...estadosFormateados]
      .filter(e => {
        const estadoNum = e.estado != null ? +e.estado : null;
        if (e.isTask) {
          if (estadoNum === 2 && !mostrarApt) return false;
          if (estadoNum === 5 && !mostrarInicioCarga) return false;
          if (estadoNum === 6 && !mostrarFinCarga) return false;
        }
        return true;
      })
      .sort((a, b) => {
        const estadoA = a.estado != null ? +a.estado : null;
        const estadoB = b.estado != null ? +b.estado : null;
        if (a.fecha === b.fecha) {
          return estadosOrden.indexOf(estadoA) - estadosOrden.indexOf(estadoB);
        }
        return a.fecha - b.fecha;
      });

    // Agrupas por día
    const agrupados = groupBy(combinados, e =>
      moment(e.fecha).format(`dddd, DD [${this.lng === 'en' ? 'of' : 'de'}] MMMM`)
    );

    // Transformas el resultado final
    return Object.keys(agrupados).map(key => ({
      dia: key,
      estados: agrupados[key].map(e => ({
        hora: moment(e.fecha).format("h:mm:ss a"),
        descripcion: getEstadoTareaToString(e.estado, e.isTask),
        valor: e.estado,
        ubigeo: !e.distrito && !e.provincia && !e.departamento
          ? null
          : `${e.distrito || ''} ${e.provincia || ''} ${e.departamento || ''}`,
        usuario: e.usuarioNombre,
        perfiles: e?.perfiles,
        motivoId: e.motivoId,
        motivo: e.motivo
      }))
    }));
  })
);
  }
}
